<template>
	<div class="sidebar-items">
		<router-link
			to="/"
			class="logo"
			:aria-label="$t('sidebar-menu-item.home')"
			v-tooltip="{
				disabled: props.isExpanded,
				content: $t('sidebar-menu-item.home')
			}"
		>
			<img
				:src="getLogoImg"
				:alt="`logo - ${$clientName}`"
				ref="logoImgRef"
				@error="imageNotLoaded"
			/>
		</router-link>

		<div class="items">
			<div>
				<nav>
					<sidebar-item
						v-if="hasTowers"
						icon="location"
						:to="{ name: 'boxes-map' }"
						:is-expanded="props.isExpanded"
						data-testid="boxes-map-link"
					>
						{{ $t("sidebar-menu-item.map") }}
					</sidebar-item>
					<sidebar-item
						v-if="hasTowers"
						data-testid="boxes-link"
						icon="tower"
						:to="{ name: 'boxes' }"
						:is-expanded="props.isExpanded"
						:routes-to-be-active="['boxes', 'box-v1', 'box']"
					>
						{{ $t("client-name-boxes") }}
					</sidebar-item>
					<sidebar-item
						v-if="hasBridges"
						data-testid="hubs-link"
						icon="bridge"
						:to="{ name: 'hubs' }"
						:is-expanded="props.isExpanded"
						:routes-to-be-active="['hubs', 'hub-v1']"
					>
						{{ $t("client-name-hubs") }}
					</sidebar-item>
					<sidebar-item
						v-if="hasTowers"
						data-testid="alarms-link"
						icon="alarm"
						:to="{ name: 'alarms' }"
						:is-expanded="props.isExpanded"
					>
						{{ $t("alarms") }}
					</sidebar-item>
					<hr v-if="!isReadOnlyUser" />
					<sidebar-item
						v-if="!isReadOnlyUser"
						icon="files"
						:to="{ name: 'file-resources-management' }"
						:is-expanded="props.isExpanded"
						data-testid="files-link"
					>
						{{ $t("views.file-resources-management.title") }}
					</sidebar-item>

					<sidebar-item
						v-if="!isReadOnlyUser"
						icon="organization"
						:to="{ name: 'organization-view' }"
						:is-expanded="props.isExpanded"
						data-testid="organization-link"
					>
						{{ $t("sidebar-menu-item.company") }}
					</sidebar-item>
				</nav>
			</div>
		</div>
		<div class="items-bottom is-hidden-mobile">
			<sidebar-expand-button
				data-testid="sidebar-expand-button"
				:aria-label="
					props.isExpanded
						? $t('sidebar-menu-item.collapse-sidepanel')
						: $t('sidebar-menu-item.expand-sidepanel')
				"
				v-tooltip="{
					content: props.isExpanded
						? $t('sidebar-menu-item.collapse-sidepanel')
						: $t('sidebar-menu-item.expand-sidepanel')
				}"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import DevicesService from "@/services/v1/Device.service";
import { useAuth } from "@/composables/useAuth";
import { useBannersHeight } from "@/composables/useBannersHeight.js";
import SidebarItem from "./sidebar-item.vue";
import SidebarExpandButton from "./sidebar-expand-button.vue";
import { useClientName } from "@/plugins/client-name/client-name.plugin";
// import { useConsoleDevices } from "@/composables/useConsoleDevices";

const { getLogoUrls } = useClientName();
// const { allDevices } = useConsoleDevices();

const props = defineProps({
	isExpanded: {
		type: Boolean,
		default: true
	}
});

const { isReadOnlyUser } = useAuth();
const { bannerHeight, initTrackBannerHeight, stopTrackBannerHeight } =
	useBannersHeight();
const cssVars = computed(() => {
	return { bannerHeight: `${bannerHeight.value}px` };
});

const logoImgRef = ref(null);
const logoUrls = ref<{
	big?: string;
	small?: string;
	defaultBig?: string;
	defaultSmall?: string;
}>({});

const allDevices = ref<any>([]);

const getLogoImg = computed(() => {
	return props.isExpanded ? logoUrls.value?.big : logoUrls.value?.small;
});

const hasTowers = computed(() => {
	return allDevices.value.some((device: any) => device.type === "TOWER");
});
const hasBridges = computed(() => {
	return allDevices.value.some((device: any) => device.type === "BRIDGE");
});
const getFallbackImg = computed(() => {
	return props.isExpanded
		? logoUrls.value?.defaultBig
		: logoUrls.value?.defaultSmall;
});

function imageNotLoaded() {
	if (logoImgRef.value) {
		logoImgRef.value.src = getFallbackImg.value;
	}
}

async function getDevices() {
	try {
		allDevices.value = await DevicesService.getDevices();
		return true;
	} catch {
		return false;
	}
}
onMounted(async () => {
	logoUrls.value = await getLogoUrls();
	await getDevices();
	initTrackBannerHeight();
});

onUnmounted(() => {
	stopTrackBannerHeight();
});
</script>

<style lang="scss" scoped>
.sidebar-items {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	background: $color-grey-100;
	overflow-y: auto;
	padding-top: 0;
	padding-bottom: 1rem;

	.logo {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		padding: 0 1rem;

		& > img {
			width: auto;
			height: $navbar-height;
		}
		&:focus-visible {
			outline: 2px auto $focus-outline-color;
			outline-offset: -2px;
		}
	}

	.items {
		flex: 1 1 0%;
		flex-direction: column;
		overflow-y: auto;
		margin-top: 1rem;

		& > div {
			overflow-y: auto;

			& > nav {
				padding: 0 0.75rem 0.25rem 0.75rem;

				a {
					margin-top: 0.25rem;
				}
			}
		}

		hr {
			background-color: $color-grey-400;
			margin: 0.5rem;
			height: 1px;
		}

		&-bottom {
			display: flex;
			justify-content: center;
			padding-bottom: calc(1.5rem + v-bind("cssVars.bannerHeight"));
		}
	}
}
</style>
